import { useEffect, useState } from 'react';

import type { IEvent } from '@/components/calendar/interfaces';

import { useGetAppointment } from '../api/get-appointment';
import type { AppointmentStatus } from '../api/types';
import { type DepositSummary, readDepositFromMetadata } from './deposit-badge';

/**
 * A booking panel's status and deposit, kept live (ENG-1306).
 *
 * The panels open with a SNAPSHOT of the calendar event, but a deposit payment
 * moves the booking server-side while the panel is open (held → confirmed,
 * deposit pending → paid). Follow the live appointment so staff never need a
 * reload — `DepositLinkActions` refreshes it the moment its link changes.
 * `setStatus` stays for the status control's own optimistic change.
 */
export function useLiveBooking(event: IEvent) {
  const [status, setStatus] = useState<AppointmentStatus | undefined>(
    typeof event.metadata?.status === 'string'
      ? (event.metadata.status as AppointmentStatus)
      : undefined
  );
  const { appointment } = useGetAppointment(String(event.id));
  const liveStatus = appointment?.status;

  useEffect(() => {
    if (liveStatus) setStatus(liveStatus);
  }, [liveStatus]);

  // `undefined` = not loaded yet (or no deposit field): keep the snapshot.
  // `null` = the booking has no deposit.
  const live = appointment?.deposit;
  const deposit: DepositSummary | null =
    live === undefined
      ? readDepositFromMetadata(event.metadata)
      : live && {
          status: live.status,
          amountCents: live.amountCents,
          currency: live.currency,
          paidAt: live.paidAt,
        };

  return { status, setStatus, deposit };
}
